/* eslint-disable max-lines */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import SelectInput from '@components/atoms/ui-kit/select-input';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { useGetAdminLicenseRequests } from '@utils/api/back-office/licensing';
import { AdminLicenseRequest } from '@utils/types/back-office/licensing';
import { LicenseRequestStatus } from '@utils/types/resellers/license-request';
import IssueLicenseModal from './issue-license-modal';
import RejectRequestModal from './reject-request-modal';

const STATUS_CLASSES: Record<LicenseRequestStatus, string> = {
  pending:
    'bg-amber-400/20 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400',
  approved:
    'bg-green-500/15 text-green-700 dark:bg-green-500/10 dark:text-green-400',
  rejected: 'bg-red-500/15 text-red-700 dark:bg-red-500/10 dark:text-red-400'
};

const STATUS_ICONS: Record<LicenseRequestStatus, typeof Clock> = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle
};

type Row = AdminLicenseRequest;

interface StatusOption {
  label: string;
  value: string;
}

export default function LicenseRequestsTab() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [statusFilter, setStatusFilter] = useState<
    LicenseRequestStatus | undefined
  >('pending');

  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<AdminLicenseRequest | null>(null);

  const { data, isLoading } = useGetAdminLicenseRequests({
    status: statusFilter
  });

  const statusOptions: StatusOption[] = useMemo(
    () => [
      { label: t('all'), value: 'all' },
      { label: t('pending'), value: 'pending' },
      { label: t('approved'), value: 'approved' },
      { label: t('rejected'), value: 'rejected' }
    ],
    [t]
  );

  const handleStatusChange = (option: StatusOption | null) => {
    if (option?.value === 'all') {
      setStatusFilter(undefined);
    } else {
      setStatusFilter(option?.value as LicenseRequestStatus);
    }
  };

  const handleIssue = useCallback((request: AdminLicenseRequest) => {
    setSelectedRequest(request);
    setIssueModalOpen(true);
  }, []);

  const handleReject = useCallback((request: AdminLicenseRequest) => {
    setSelectedRequest(request);
    setRejectModalOpen(true);
  }, []);

  const columns: DataGridColumn<Row>[] = useMemo(
    () => [
      {
        id: 'id',
        header: t('id'),
        accessorKey: 'id',
        maxWidth: 80,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        serverOperator: 'eq',
        cell: ({ getValue }) => `#${getValue()}`
      },
      {
        id: 'reseller_org_name',
        header: t('reseller'),
        accessorKey: 'reseller_org_name',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => (
          <span className="font-medium">{String(getValue())}</span>
        )
      },
      {
        id: 'target_org_name',
        header: t('target-org'),
        accessorKey: 'target_org_name',
        minWidth: 150,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue())
      },
      {
        id: 'limits',
        header: t('limits'),
        accessorKey: 'org_limit',
        minWidth: 130,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        serverOperator: 'gte',
        cell: ({ row }) => (
          <span className="text-sm">
            {row.original.org_limit} {t('orgs')} / {row.original.user_limit}{' '}
            {t('users')}
          </span>
        )
      },
      {
        id: 'max_processing',
        header: t('processing'),
        accessorKey: 'max_processing',
        maxWidth: 100,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        serverOperator: 'gte',
        cell: ({ getValue }) => `${getValue()} ${t('slots')}`
      },
      {
        id: 'status',
        header: t('status'),
        accessorKey: 'status',
        minWidth: 120,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const status = getValue() as LicenseRequestStatus;
          const Icon = STATUS_ICONS[status];
          return (
            <span
              className={cn(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                STATUS_CLASSES[status]
              )}
            >
              <Icon className="h-3 w-3" />
              {t(status)}
            </span>
          );
        }
      },
      {
        id: 'notes',
        header: t('notes'),
        accessorKey: 'notes',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const notes = getValue() as string | null;
          if (!notes) return '-';
          return (
            <span
              className="text-xs truncate block max-w-[180px]"
              title={notes}
            >
              {notes}
            </span>
          );
        }
      },
      {
        id: 'created_at',
        header: t('created'),
        accessorKey: 'created_at',
        minWidth: 130,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => {
          const date = getValue() as string;
          if (!date) return '-';
          try {
            return formatDistanceToNow(new Date(date), { addSuffix: true });
          } catch {
            return '-';
          }
        }
      }
    ],
    [t]
  );

  const actions: GridAction<Row>[] = useMemo(
    () => [
      {
        id: 'issue',
        label: t('issue'),
        icon: <CheckCircle className="h-4 w-4" />,
        intent: 'primary',
        run: handleIssue,
        isVisible: (row?: Row) => row?.status === 'pending'
      },
      {
        id: 'reject',
        label: t('reject'),
        icon: <XCircle className="h-4 w-4" />,
        intent: 'danger',
        run: handleReject,
        isVisible: (row?: Row) => row?.status === 'pending'
      }
    ],
    [t, handleIssue, handleReject]
  );

  const rows = data?.result ?? [];
  const totalItems = data?.total_results ?? 0;

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4">
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('review-and-process-license-requests')}
        </p>
        <div className="flex items-center gap-3">
          <span
            className="text-xs px-2.5 py-1 rounded-full border border-hairline bg-app-soft"
            style={{ color: getColour('normalText') }}
          >
            {totalItems} {t('total-requests')}
          </span>
          <div className="w-40">
            <SelectInput
              size="small"
              options={statusOptions}
              value={statusOptions.find(
                opt => opt.value === (statusFilter ?? 'all')
              )}
              onChange={handleStatusChange}
              placeholder={t('filter-by-status')}
            />
          </div>
        </div>
      </div>

      <DataGridV2<Row>
        data={rows}
        columns={columns}
        actions={actions}
        enableRowSelection={false}
        enablePagination
        manualPagination
        enableSorting
        manualSorting
        enableFiltering
        manualFiltering
        totalItems={totalItems}
        pageSize={25}
        pageSizeOptions={[25, 50, 75, 100]}
        showColumnFilters
        filterMode="popover"
        fill
        dense
        grid={false}
        striped
        loading={isLoading}
        getRowId={row => String(row.id)}
      />

      <IssueLicenseModal
        isOpen={issueModalOpen}
        onClose={() => {
          setIssueModalOpen(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
      />

      <RejectRequestModal
        isOpen={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
      />
    </div>
  );
}
